import React, { Suspense, useRef, useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import {
  Stars,
  Float,
  Text,
  Line,
  Billboard,
  OrbitControls,
} from "@react-three/drei";
import * as THREE from "three";
import config from "../../config";
import "./PlanCosmosView.css";
import { useSelector } from "react-redux";

const MS_IN_DAY = 1000 * 60 * 60 * 24;

const CATEGORY_COLORS = [
  "#6366f1", "#22c55e", "#f59e0b", "#ef4444",
  "#06b6d4", "#a855f7", "#ec4899", "#14b8a6",
];

const isMobile = () =>
  typeof window !== "undefined" && window.innerWidth < 768;

/* ─────────────────────────────────────────────
   CURVE — bottom-right origin → top-left promised day, increasing depth
───────────────────────────────────────────── */
const buildCurve = () =>
  new THREE.CatmullRomCurve3([
    new THREE.Vector3( 30, -8,   0),
    new THREE.Vector3( 15, -5,  -8),
    new THREE.Vector3(  0, -2, -18),
    new THREE.Vector3(-15,  1, -30),
    new THREE.Vector3(-30,  4, -44),
    new THREE.Vector3(-45,  7, -60),
    new THREE.Vector3(-60, 10, -78),
  ]);

/* ─────────────────────────────────────────────
   CAMERA CONTROLLER
───────────────────────────────────────────── */
const CameraController = ({ curve, nodePositions }) => {
  const { camera } = useThree();
  const controlsRef = useRef();
  const mobile = isMobile();
  const didSetup = useRef(false);

  // Run every frame until nodePositions are available, then lock
  useFrame(() => {
    if (didSetup.current) return;
    if (!controlsRef.current) return;
    if (nodePositions.length === 0) return; // wait for nodes

    didSetup.current = true;

    const endPoint = curve.getPoint(1);
    const originPoint = curve.getPoint(0);

    const allPositions = [
      ...nodePositions,
      [endPoint.x, endPoint.y, endPoint.z],
      [originPoint.x, originPoint.y, originPoint.z],
    ];

    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;

    allPositions.forEach(([x, y, z]) => {
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
      minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z);
    });

    const cx = (minX + maxX) / 2;
    const cy = minY + (maxY - minY) * 0.4;
    const cz = (minZ + maxZ) / 2;

    const sizeX = maxX - minX;
    const sizeY = maxY - minY;
    const sizeZ = maxZ - minZ;
    const sceneSpan = Math.max(sizeX, sizeY, sizeZ, 20);

    const pullback = mobile ? sceneSpan * 1.4 : sceneSpan * 1.1;
    const height   = mobile ? sceneSpan * 0.1 : sceneSpan * 0.15;

   if (mobile) {
  camera.position.set(cx+55, cy , cz+55 );  // ← tweak these to taste
  controlsRef.current.target.set(cx, cy - 2, cz);
  controlsRef.current.update();
} else {
  const pullback = sceneSpan * 1.1;
  const height   = sceneSpan * 0.15;
   camera.position.set(cx+55, cy+10 , cz+75 ); 
  controlsRef.current.target.set(cx, cy, cz);
  controlsRef.current.update();
}

  });

  return (
    <OrbitControls
      ref={controlsRef}
      makeDefault
      enablePan={true}
      enableZoom={true}
      enableRotate={true}
      zoomSpeed={mobile ? 1.2 : 0.8}
      panSpeed={mobile ? 0.8 : 0.6}
      rotateSpeed={mobile ? 0.4 : 0.5}
      minDistance={2}
      maxDistance={300}
      enableDamping={true}
      dampingFactor={0.05}
    />
  );
};

/* ─────────────────────────────────────────────
   SINGLE NODE
───────────────────────────────────────────── */
const CosmosNode = ({
  post, position, color, isPast, onHover, onLeave, onClick,
  entryRef, entryDelay,
}) => {
  const meshRef = useRef();
  const glowRef = useRef();
  const groupRef = useRef();
  const [hovered, setHovered] = useState(false);
  const updateCount = post.updates?.length || 0;
  const baseSize = isPast ? 0.22 + updateCount * 0.04 : 0.13;
  const mobile = isMobile();

  const originPoint = useMemo(() => buildCurve().getPoint(0), []);

  useFrame((state) => {
    if (!meshRef.current || !groupRef.current) return;
    const t = state.clock.getElapsedTime();

    const rawProgress = entryRef.current;
    const localProgress = Math.max(
      0,
      Math.min((rawProgress - entryDelay) / (1 - entryDelay * 0.5 + 0.001), 1)
    );
    const eased = 1 - Math.pow(1 - localProgress, 3);

    groupRef.current.position.set(
      originPoint.x + (position[0] - originPoint.x) * eased,
      originPoint.y + (position[1] - originPoint.y) * eased,
      originPoint.z + (position[2] - originPoint.z) * eased,
    );

    const minScale = mobile ? 0.4 : 0.001;
    const arrivalScale = Math.max(eased, minScale);

    meshRef.current.rotation.y = t * 0.4;
    meshRef.current.rotation.x = t * 0.2;
    const pulse = 1 + Math.sin(t * 1.4 + position[0]) * 0.06;
    meshRef.current.scale.setScalar((hovered ? 1.5 : pulse) * arrivalScale);

    if (glowRef.current) {
      const baseOpacity = mobile ? 0.25 : 0.12;
      glowRef.current.material.opacity =
        (isPast ? baseOpacity + Math.sin(t * 1.2) * 0.06 : 0.08) * arrivalScale;
    }
  });

  return (
    <Float speed={1.4} rotationIntensity={0} floatIntensity={isPast ? 0.4 : 0.15}>
      <group ref={groupRef}>
        <mesh ref={glowRef}>
          <sphereGeometry args={[baseSize * (mobile ? 4 : 2.5), 12, 12]} />
          <meshBasicMaterial
            color={color}
            transparent
            opacity={mobile ? 0.25 : 0.1}
            depthWrite={false}
          />
        </mesh>

        {mobile && (
          <mesh
            onPointerDown={(e) => {
              e.stopPropagation();
              onHover(post);
              onClick(post);
            }}
          >
            <sphereGeometry args={[baseSize * 3, 8, 8]} />
            <meshBasicMaterial transparent opacity={0} depthWrite={false} />
          </mesh>
        )}

        <mesh
          ref={meshRef}
          onPointerOver={(e) => {
            e.stopPropagation();
            setHovered(true);
            onHover(post);
            document.body.style.cursor = "pointer";
          }}
          onPointerOut={(e) => {
            e.stopPropagation();
            setHovered(false);
            onLeave();
            document.body.style.cursor = "default";
          }}
          onPointerDown={(e) => {
            if (mobile) {
              e.stopPropagation();
              setHovered(true);
              onHover(post);
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            onClick(post);
          }}
        >
          <icosahedronGeometry args={[baseSize, isPast ? 2 : 1]} />
          <meshStandardMaterial
            color={color}
            emissive={color}
            emissiveIntensity={isPast ? 0.9 : 0.3}
            roughness={0.3}
            metalness={0.6}
            transparent={!isPast}
            opacity={isPast ? 1 : 0.5}
            wireframe={!isPast}
          />
        </mesh>

        {isPast && updateCount > 0 &&
          Array.from({ length: Math.min(updateCount, 3) }).map((_, i) => (
            <mesh key={i} rotation={[Math.PI / 2, (i * Math.PI) / 3, 0]}>
              <torusGeometry args={[baseSize * (1.8 + i * 0.5), 0.012, 6, 48]} />
              <meshBasicMaterial color={color} transparent opacity={0.25 - i * 0.06} />
            </mesh>
          ))
        }

        {hovered && (
          <Billboard follow lockX={false} lockY={false} lockZ={false}>
            <Text
              position={[0, baseSize * 3, 0]}
              fontSize={mobile ? 0.28 : 0.18}
              color="white"
              anchorX="center"
              anchorY="bottom"
              outlineWidth={0.01}
              outlineColor="#000"
            >
              {post.category}
            </Text>
          </Billboard>
        )}
      </group>
    </Float>
  );
};

/* ─────────────────────────────────────────────
   PATH LINE
───────────────────────────────────────────── */
const PathLine = ({ curve, progressT, entryRef }) => {
  const allPoints = useMemo(() => curve.getPoints(120), [curve]);
  const pastPoints = useMemo(
    () => curve.getPoints(120).slice(0, Math.floor(progressT * 120) + 1),
    [curve, progressT]
  );

  const dimLineRef = useRef();
  const pastLineRef = useRef();
  const [dimVisible, setDimVisible] = useState(
    [allPoints[0], allPoints[1]].map(p => [p.x, p.y, p.z])
  );
  const [pastVisible, setPastVisible] = useState([]);
  const dimCountRef = useRef(2);
  const pastCountRef = useRef(0);

  useFrame(() => {
    const p = entryRef.current;

    const dimProgress = Math.min(p / 0.5, 1);
    const dimTarget = Math.max(2, Math.floor(dimProgress * allPoints.length));
    if (dimTarget !== dimCountRef.current) {
      dimCountRef.current = dimTarget;
      setDimVisible(allPoints.slice(0, dimTarget).map(pt => [pt.x, pt.y, pt.z]));
    }

    const pastProgress = Math.max(0, Math.min((p - 0.3) / 0.6, 1));
    const pastTarget = Math.max(0, Math.floor(pastProgress * pastPoints.length));
    if (pastTarget !== pastCountRef.current && pastPoints.length > 0) {
      pastCountRef.current = pastTarget;
      if (pastTarget >= 2) {
        setPastVisible(pastPoints.slice(0, pastTarget).map(pt => [pt.x, pt.y, pt.z]));
      }
    }

    if (dimLineRef.current?.material) {
      dimLineRef.current.material.opacity = Math.min(dimProgress * 0.08, 0.04);
    }
    if (pastLineRef.current?.material) {
      pastLineRef.current.material.opacity = Math.min(pastProgress * 0.7, 0.55);
    }
  });

  return (
    <group>
      {dimVisible.length >= 2 && (
        <Line
          ref={dimLineRef}
          points={dimVisible}
          color="#ffffff"
          lineWidth={0.5}
          transparent
          opacity={0}
          dashed
          dashScale={2}
          dashSize={1}
          gapSize={1}
        />
      )}
      {pastVisible.length >= 2 && (
        <Line
          ref={pastLineRef}
          points={pastVisible}
          color="rgb(241,117,1)"
          lineWidth={1.2}
          transparent
          opacity={0}
        />
      )}
    </group>
  );
};

/* ─────────────────────────────────────────────
   PROMISED DAY SPHERE
───────────────────────────────────────────── */
const PromisedDay = ({ position, daysLeft, formatDate, date }) => {
  const ringRef = useRef();
  const coreRef = useRef();

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (ringRef.current) ringRef.current.rotation.z = t * 0.3;
    if (coreRef.current) {
      const s = 1 + Math.sin(t * 1.1) * 0.08;
      coreRef.current.scale.setScalar(s);
    }
  });

  return (
    <group position={position}>
      {[4, 6, 9].map((r, i) => (
        <mesh key={i}>
          <sphereGeometry args={[r, 16, 16]} />
          <meshBasicMaterial
            color="rgb(241,117,1)"
            transparent
            opacity={0.015 - i * 0.004}
            depthWrite={false}
          />
        </mesh>
      ))}

      <mesh ref={ringRef} rotation={[Math.PI / 4, 0, 0]}>
        <torusGeometry args={[2.2, 0.04, 8, 80]} />
        <meshBasicMaterial color="rgb(241,117,1)" transparent opacity={0.3} />
      </mesh>

      <mesh ref={coreRef}>
        <sphereGeometry args={[0.9, 24, 24]} />
        <meshStandardMaterial
          color="rgb(241,117,1)"
          emissive="rgb(241,117,1)"
          emissiveIntensity={1.2}
          roughness={0.1}
          metalness={0.8}
        />
      </mesh>

      <Billboard>
        <Text position={[0, 3, 0]} fontSize={0.28} color="rgba(255,255,255,0.9)" anchorX="center" outlineWidth={0.015} outlineColor="#000">
          Promised Day
        </Text>
        <Text position={[0, 2.5, 0]} fontSize={0.18} color="rgba(241,117,1,0.8)" anchorX="center">
          {formatDate(date)}
        </Text>
        <Text position={[0, 2.1, 0]} fontSize={0.15} color="rgba(255,255,255,0.3)" anchorX="center">
          {daysLeft} days away
        </Text>
      </Billboard>
    </group>
  );
};

/* ─────────────────────────────────────────────
   ORIGIN MARKER
───────────────────────────────────────────── */
const OriginMarker = ({ position, label }) => (
  <group position={position}>
    <mesh>
      <sphereGeometry args={[0.18, 16, 16]} />
      <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={0.8} />
    </mesh>
    <Billboard>
      <Text position={[0, 0.6, 0]} fontSize={0.18} color="rgba(255,255,255,0.4)" anchorX="center">
        {label}
      </Text>
    </Billboard>
  </group>
);

/* ─────────────────────────────────────────────
   TODAY MARKER
───────────────────────────────────────────── */
const TodayMarker = ({ position, daysPassed }) => {
  const ref = useRef();
  useFrame((state) => {
    if (ref.current) {
      const s = 1 + Math.sin(state.clock.getElapsedTime() * 2.5) * 0.15;
      ref.current.scale.setScalar(s);
    }
  });

  return (
    <group position={position}>
      <mesh ref={ref}>
        <sphereGeometry args={[0.22, 16, 16]} />
        <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={1.5} />
      </mesh>
      <Billboard>
        <Text position={[0, 0.7, 0]} fontSize={0.2} color="white" anchorX="center">
          Day {daysPassed} · You are here
        </Text>
      </Billboard>
    </group>
  );
};

/* ─────────────────────────────────────────────
   NODE TENDRIL
───────────────────────────────────────────── */
const NodeTendril = ({
  nodeT, nodeIndex, totalNodes,
  realLatestT, mockedTargetT, tendrilT,
  curve, color, progressT, nodePosition,
  entryRef, entryDelay,
}) => {
  const lineRef = useRef();

  // Bake at the FULL extent (mocked target). Stable across phase 2 — no morph.
  const maxEndT = Math.min(
    Math.max(mockedTargetT, realLatestT),
    Math.max(progressT, realLatestT)
  );

  const allPoints = useMemo(() => {
    if (maxEndT <= nodeT) return [];

    const steps = 120;
    const pts = [];
    const baseAngle = (nodeIndex / Math.max(totalNodes, 1)) * Math.PI * 2;

    for (let i = 0; i <= steps; i++) {
      const t = nodeT + (maxEndT - nodeT) * (i / steps);
      const base = curve.getPoint(t);
      const tangent = curve.getTangent(t).normalize();

      const worldUp = new THREE.Vector3(0, 1, 0);
      const normal = new THREE.Vector3().crossVectors(tangent, worldUp).normalize();
      const binormal = new THREE.Vector3().crossVectors(tangent, normal).normalize();

      const progress = i / steps;
      const convergeFactor = 1 - Math.pow(maxEndT, 2.5) * 0.85;
      const cylinderRadius = 0.7 * convergeFactor;
      const blendT = Math.min(progress / 0.15, 1);

      const pathPoint = curve.getPoint(nodeT);
      const nodeOffset = new THREE.Vector3(
        nodePosition[0] - pathPoint.x,
        nodePosition[1] - pathPoint.y,
        nodePosition[2] - pathPoint.z,
      );

      const angle = baseAngle + progress * Math.PI * 3;
      const cylinderOffset = new THREE.Vector3(
        normal.x * Math.cos(angle) * cylinderRadius + binormal.x * Math.sin(angle) * cylinderRadius,
        normal.y * Math.cos(angle) * cylinderRadius + binormal.y * Math.sin(angle) * cylinderRadius,
        normal.z * Math.cos(angle) * cylinderRadius + binormal.z * Math.sin(angle) * cylinderRadius,
      );

      const finalOffset = new THREE.Vector3().lerpVectors(nodeOffset, cylinderOffset, blendT);

      pts.push([
        base.x + finalOffset.x,
        base.y + finalOffset.y,
        base.z + finalOffset.z,
      ]);
    }

    return pts;
  }, [nodeT, maxEndT, curve, nodePosition, nodeIndex, totalNodes]);

  // Fraction of allPoints that represent the REAL portion (pre-preview)
  const realFraction = maxEndT > nodeT
    ? Math.max(0, Math.min((realLatestT - nodeT) / (maxEndT - nodeT), 1))
    : 0;

  const visibleCountRef = useRef(0);
  const [visiblePoints, setVisiblePoints] = useState([]);

  useEffect(() => {
    if (allPoints.length < 2) return;
    if (visibleCountRef.current < 2) return;
    const count = Math.min(visibleCountRef.current, allPoints.length);
    setVisiblePoints(allPoints.slice(0, count));
  }, [allPoints]);

  useFrame(() => {
    if (allPoints.length < 2) return;

    const rawProgress = entryRef.current;
    const drawStart = entryDelay + 0.25;
    const entryDrawProgress = Math.max(
      0,
      Math.min((rawProgress - drawStart) / 0.6, 1)
    );

    // Trace fraction = real portion grown by tendrilT toward 1
    const traceFraction = realFraction + (1 - realFraction) * tendrilT;
    const overallFraction = entryDrawProgress * traceFraction;
    const targetCount = Math.max(2, Math.floor(overallFraction * allPoints.length));

    if (targetCount !== visibleCountRef.current) {
      visibleCountRef.current = targetCount;
      setVisiblePoints(allPoints.slice(0, targetCount));
    }

    if (lineRef.current?.material) {
      lineRef.current.material.opacity = Math.min(entryDrawProgress * 2, 0.45);
    }
  });

  if (visiblePoints.length < 2) return null;

  return (
    <Line
      ref={lineRef}
      points={visiblePoints}
      color={color}
      lineWidth={1.0}
      transparent
      opacity={0}
    />
  );
};

/* ─────────────────────────────────────────────
   SCENE
───────────────────────────────────────────── */
const Scene = ({ plan, previewActive, previewMonths = 6, onNodeClick, onNodeHover, onNodeLeave }) => {
  const curve = useMemo(() => buildCurve(), []);
  const entryRef = useRef(0);

  const totalDays = (plan.durationInYears || 1) * 365;
  const startDate = new Date(plan.createdAt);
  const today = new Date();
  const promisedDay = new Date(startDate.getTime() + totalDays * MS_IN_DAY);
  const daysPassed = Math.max(Math.floor((today - startDate) / MS_IN_DAY), 0);
  const daysLeft = Math.max(totalDays - daysPassed, 0);
  const realProgressT = Math.min(daysPassed / totalDays, 1);
  const targetT = previewActive
    ? Math.min((daysPassed + previewMonths * 30) / totalDays, 1)
    : realProgressT;

  const [displayT, setDisplayT] = useState(realProgressT);
  const [tendrilT, setTendrilT] = useState(0);
  const previewTargetTRef = useRef(targetT);
  previewTargetTRef.current = previewActive
    ? Math.min((daysPassed + previewMonths * 30) / totalDays, 1)
    : realProgressT;
  const realProgressTRef = useRef(realProgressT);
  realProgressTRef.current = realProgressT;
  const previewActiveRef = useRef(previewActive);
  previewActiveRef.current = previewActive;
  const displayTRef = useRef(displayT);
  displayTRef.current = displayT;
  const tendrilTRef = useRef(tendrilT);
  tendrilTRef.current = tendrilT;

  useFrame((_, delta) => {
    if (entryRef.current < 1) {
      entryRef.current = Math.min(entryRef.current + delta * 0.45, 1);
    }

    // Phase order:
    //  Forward (preview ON):  displayT extends → then tendrilT extends
    //  Reverse (preview OFF): tendrilT retracts → then displayT retracts
    setTendrilT((prev) => {
      let target;
      if (previewActiveRef.current) {
        const pathSettled =
          Math.abs(displayTRef.current - previewTargetTRef.current) < 0.005;
        target = pathSettled ? 1 : prev;
      } else {
        target = 0;
      }
      if (Math.abs(prev - target) < 0.005) return prev === target ? prev : target;
      const speed = 0.45;
      const step = delta * speed;
      return prev < target
        ? Math.min(prev + step, target)
        : Math.max(prev - step, target);
    });

    setDisplayT((prev) => {
      let target;
      if (previewActiveRef.current) {
        target = previewTargetTRef.current;
      } else {
        const tendrilRetracted = tendrilTRef.current < 0.01;
        target = tendrilRetracted ? realProgressTRef.current : prev;
      }
      if (Math.abs(prev - target) < 0.0008) return prev === target ? prev : target;
      const speed = 0.35;
      const step = delta * speed;
      return prev < target
        ? Math.min(prev + step, target)
        : Math.max(prev - step, target);
    });
  });

  const progressT = displayT;

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });

  const posts = useMemo(
    () => (plan.posts || []).map((p) => p.postId).filter(Boolean),
    [plan]
  );

  const categories = useMemo(() => {
    const seen = new Set();
    return posts.reduce((acc, p) => {
      if (!seen.has(p.category)) { seen.add(p.category); acc.push(p.category); }
      return acc;
    }, []);
  }, [posts]);

  const positionedNodes = useMemo(() => {
    const previewOffsetT = (previewMonths * 30) / totalDays;
    return posts.map((post, i) => {
      const postDate = new Date(post.date || plan.createdAt);
      const t = Math.min(
        Math.max((postDate - startDate) / (promisedDay - startDate), 0.02),
        0.92
      );
      const point = curve.getPoint(t);

      const spread = 1.8;
      const angle = (i / posts.length) * Math.PI * 2 + i * 1.2;
      const offsetX = Math.cos(angle) * spread * (0.5 + (((i * 7 + 13) % 10) / 10) * 0.5);
      const offsetY = Math.sin(angle) * spread * 0.6;

      const isPast = t <= progressT;
      const catIdx = categories.indexOf(post.category) % CATEGORY_COLORS.length;
      const color = CATEGORY_COLORS[Math.max(catIdx, 0)];

      let latestUpdateT = null;
      if (post.updates?.length > 0) {
        const latestDate = post.updates.reduce((latest, u) => {
          const d = new Date(u.date);
          return d > latest ? d : latest;
        }, new Date(0));

        latestUpdateT = Math.min(
          Math.max((latestDate - startDate) / (promisedDay - startDate), t),
          1
        );
      }

      const baseLatest = latestUpdateT !== null ? latestUpdateT : t;
      const mockedTargetT = Math.min(baseLatest + previewOffsetT, 1);

      return {
        post, t, isPast, color,
        realLatestT: baseLatest,
        mockedTargetT,
        position: [point.x + offsetX, point.y + offsetY, point.z],
        index: i,
      };
    });
  }, [posts, curve, progressT, previewMonths, totalDays]);

  const nodePositions = useMemo(
    () => positionedNodes.map(n => n.position),
    [positionedNodes]
  );

  const todayPoint = curve.getPoint(progressT);
  const originPoint = curve.getPoint(0);
  const endPoint = curve.getPoint(1);

  return (
    <>
      <CameraController curve={curve} nodePositions={nodePositions} />

      <ambientLight intensity={0.15} />
      <pointLight position={[endPoint.x, endPoint.y + 3, endPoint.z]} intensity={6} color="rgb(241,117,1)" distance={40} decay={2} />
      <pointLight position={[originPoint.x, 3, originPoint.z]} intensity={2} color="#6366f1" distance={30} decay={2} />
      <pointLight position={[todayPoint.x, 2, todayPoint.z]} intensity={3} color="#ffffff" distance={20} decay={2} />

      <Stars
        radius={120}
        depth={60}
        count={isMobile() ? 2000 : 5000}
        factor={3}
        saturation={0.3}
        fade
        speed={0.6}
      />

      {/* No rotation — direction baked into curve */}
      <group>
        <PathLine curve={curve} progressT={progressT} entryRef={entryRef} />

        {positionedNodes.map(({ post, t, color, realLatestT, mockedTargetT, isPast, position, index }) =>
          isPast ? (
            <NodeTendril
              key={`tendril-${post._id}`}
              nodeT={t}
              nodeIndex={index}
              totalNodes={positionedNodes.length}
              realLatestT={realLatestT}
              mockedTargetT={mockedTargetT}
              tendrilT={tendrilT}
              curve={curve}
              color={color}
              progressT={progressT}
              nodePosition={position}
              entryRef={entryRef}
              entryDelay={index * 0.08}
            />
          ) : null
        )}

        <OriginMarker
          position={[originPoint.x, originPoint.y, originPoint.z]}
          label={formatDate(startDate)}
        />

        <TodayMarker
          position={[todayPoint.x, todayPoint.y, todayPoint.z]}
          daysPassed={Math.floor(progressT * totalDays)}
        />

        <PromisedDay
          position={[endPoint.x, endPoint.y, endPoint.z]}
          daysLeft={daysLeft}
          formatDate={formatDate}
          date={promisedDay}
        />

        {positionedNodes.map(({ post, position, isPast, color, index }) => (
          <CosmosNode
            key={post._id}
            post={post}
            position={position}
            color={color}
            isPast={isPast}
            onHover={onNodeHover}
            onLeave={onNodeLeave}
            onClick={onNodeClick}
            entryRef={entryRef}
            entryDelay={index * 0.08}
          />
        ))}
      </group>
    </>
  );
};

/* ─────────────────────────────────────────────
   ROOT COMPONENT
───────────────────────────────────────────── */
const PREVIEW_MONTHS_BY_DURATION = { 1: 3, 2: 6, 3: 6, 4: 12, 5: 12 };

const PlanCosmosView = () => {
  const { planId } = useParams();
  const navigate = useNavigate();

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredPost, setHoveredPost] = useState(null);
  const [previewActive, setPreviewActive] = useState(false);

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const { data } = await axios.get(
          `${config.apiUrl}/api/v1/yearly-plan/${planId}`,
          { withCredentials: true }
        );
        setPlan(data.plan);
      } catch {
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetchPlan();
  }, [planId]);

  useEffect(() => {
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, []);

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });

  const totalDays = plan ? (plan.durationInYears || 1) * 365 : 0;
  const startDate = plan ? new Date(plan.createdAt) : new Date();
  const today = new Date();
  const daysPassed = Math.max(Math.floor((today - startDate) / MS_IN_DAY), 0);
  const daysLeft = Math.max(totalDays - daysPassed, 0);
  const progressPercent = plan ? Math.min((daysPassed / totalDays) * 100, 100) : 0;
  const { user } = useSelector((state) => state.user);

  if (loading) {
    return (
      <div className="pc-loading">
        <div className="pc-loader" />
        <span className="pc-loading-text">Assembling your cosmos…</span>
      </div>
    );
  }

  return (
    <div className="pc-root">
      <Helmet>
        <title>{`Cosmos · ${plan?.durationInYears}Y Vision`}</title>
      </Helmet>

      <Canvas
        className="pc-canvas"
        dpr={Math.min(typeof window !== "undefined" ? window.devicePixelRatio : 1, 2)}
        camera={{
          position: [40, 20, 100],
          fov: isMobile() ? 85 : 70,
          near: 0.1,
          far: 600,
        }}
        gl={{ antialias: !isMobile(), powerPreference: "high-performance" }}
      >
        <Suspense fallback={null}>
          <Scene
            plan={plan}
            previewActive={previewActive}
            previewMonths={PREVIEW_MONTHS_BY_DURATION[plan?.durationInYears] || 6}
            onNodeClick={(post) => navigate(`/dashboard/note-details/${post._id}`)}
            onNodeHover={setHoveredPost}
            onNodeLeave={() => setHoveredPost(null)}
          />
        </Suspense>
      </Canvas>

      <div className="pc-topbar">
        <div className="pc-progress-pill">
            <div className="pc-pill-header">
                <span className="pc-pill-name">{(user?.name?.trim()?.split(" ")[0] || "Your")}'s</span>
                <span className="pc-pill-x">✦</span>
                <span className="pc-pill-year">{plan?.durationInYears}Y Vision</span>
            </div>

            <div className="pc-pill-categories">
                {[...new Set((plan?.posts || []).map(p => p.postId?.category).filter(Boolean))].map((cat, i) => (
                <div key={cat} className="pc-pill-cat-row">
                    <span
                        className="pc-pill-dot"
                        style={{ background: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }}
                    />
                    <span className="pc-pill-cat-label">{cat}</span>
                    </div>
                ))}
            </div>

            <div className="pc-pill-progress">
            <div className="pc-progress-fill" style={{ width: `${progressPercent}%` }} />
            <span className="pc-progress-text">
                {progressPercent.toFixed(1)}% · {daysLeft}d left
            </span>
            </div>
        </div>

        </div>



      {hoveredPost && (
        <div className="pc-hovercard">
          <div className="pc-hovercard-header">
            <span className="pc-hovercard-category">{hoveredPost.category}</span>
            <span className={`pc-hovercard-status ${hoveredPost.status}`}>
              {hoveredPost.status}
            </span>
          </div>
          <p className="pc-hovercard-note">
            {hoveredPost.note?.length > 110
              ? hoveredPost.note.substring(0, 110) + "…"
              : hoveredPost.note}
          </p>
          {hoveredPost.updates?.length > 0 && (
            <div className="pc-hovercard-updates">
              {hoveredPost.updates.slice(0, 3).map((u) => (
                <div key={u._id} className="pc-hovercard-update">
                  <span className="pc-hovercard-update-date">{formatDate(u.date)}</span>
                  <span className="pc-hovercard-update-text">{u.update}</span>
                </div>
              ))}
            </div>
          )}
          <span className="pc-hovercard-hint">Click to explore →</span>
        </div>
      )}

      <button
        type="button"
        className={`pc-future-btn ${previewActive ? "active" : ""}`}
        onClick={() => setPreviewActive((v) => !v)}
      >
        <span className="pc-future-btn-icon">✦</span>
        <span className="pc-future-btn-text">
          <span className="pc-future-btn-eyebrow">
            {previewActive ? "Previewing" : "Preview"}
          </span>
          <span className="pc-future-btn-label">
            {previewActive
              ? "Back to today"
              : `How this looks in ${PREVIEW_MONTHS_BY_DURATION[plan?.durationInYears] || 6} months`}
          </span>
        </span>
        <span className="pc-future-btn-arrow">{previewActive ? "↺" : "→"}</span>
      </button>

      <div className="pc-space-label">
        <p className="pc-space-label-line">your records in space</p>
        <p className="pc-space-label-sub">each one reaching toward the future you wrote</p>
      </div>

      <div className="pc-hint">
        <span>
          {isMobile()
            ? "✦ Pinch to zoom · Tap records to explore"
            : "🖱 Drag to look around · Hover records · Click to enter"}
        </span>
      </div>
    </div>
  );
};

export default PlanCosmosView;
